import { cx } from "@linaria/core";
import { useRef } from "react";

import type { PageDescriptor } from "@pm2/api";
import { textColumnCls } from "@pm2/react-utils";
import { Link } from "@provider/navigation";
import { printHtmlToJsx, simpleHtmlParsers } from "@utils/ast";
import { useKatex } from "@utils/react";

import * as css from "./atom.course-table-of-contents.style";

type Props = {
  pageDescriptor: PageDescriptor;
};

/**
 * table of contents for a complete course, iterating through all nested
 * chapters and chapter groups
 */
export const CourseTableOfContents: React.FC<Props> = ({ pageDescriptor }) => {
  const ref = useRef<HTMLOListElement>(null);

  useKatex(ref, [pageDescriptor.id]);

  // only allow printing table of contents for actual courses
  if (pageDescriptor.type !== "Course") {
    return null;
  }

  // don't render anything if the course is completely empty (in that case the
  // table of contents wouldn't add anything meaningful anyway)
  if (!pageDescriptor.children || !pageDescriptor.children.length) {
    return null;
  }

  return (
    <ol
      ref={ref}
      className={cx(textColumnCls, css.tableOfContentsCls, css.listCls)}
    >
      {pageDescriptor.children.map((childDescriptor) => (
        <CourseItem key={childDescriptor.id} pageDescriptor={childDescriptor} />
      ))}
    </ol>
  );
};

/**
 * renders a single item within the table of contents; if the item is a
 * chapter, then a link is inserted, otherwise all nested chapters inside
 * chapter groups are added.
 */
const CourseItem: React.FC<Props> = ({ pageDescriptor }) => {
  if (pageDescriptor.type === "Chapter") {
    return (
      <li>
        <Link className={css.linkCls} href={pageDescriptor.path}>
          {printHtmlToJsx(pageDescriptor.title, simpleHtmlParsers)}
        </Link>
      </li>
    );
  }

  // only nest chapter groups that actually contain children (otherwise simply
  // hide this item from the table of contents, as it's completely empty)
  if (
    pageDescriptor.type !== "ChapterGroup" ||
    !pageDescriptor.children?.length
  ) {
    return null;
  }

  return (
    <li>
      <b>{printHtmlToJsx(pageDescriptor.title, simpleHtmlParsers)}</b>
      <ol className={css.listCls}>
        {pageDescriptor.children.map((childDescriptor) => (
          <CourseItem
            key={childDescriptor.id}
            pageDescriptor={childDescriptor}
          />
        ))}
      </ol>
    </li>
  );
};
